// @flow

import { omitBy, constant, get, keyBy, identity, map, mapValues, set } from 'lodash/fp';

import createReducer from '../utils/createReducer';
import { API_CALL_FAILURE, API_CALL_REQUEST, API_CALL_SUCCESS } from '../constants/actionTypes';
import {
  CHALLENGE,
  MY_COMPLETED_CHALLENGES,
  MY_LIVE_CHALLENGES,
  MY_UPCOMING_CHALLENGES,
  DELETE_CHALLENGES,
  CHALLENGE_ENTRIES
} from '../constants/apiTypes';
import decodeChallenge from '../normalizers/decodeChallenge';
import decodeChallengeEntry from '../normalizers/decodeChallengeEntry';
import { mapErrorMessages } from '../utils/challengeUtils';

const initialState = {
  isDeletingChallenges: false,
  deletingIds: [],
  entries: {},
  successMessage: null,
  ...mapValues(
    constant({
      challenges: {},
      currentPage: 1,
      errorMessages: [],
      isFetching: true,
      hasInitialised: false,
      hasNextPage: false,
      lastPage: 1
    }),
    keyBy(identity, ['completed', 'live', 'upcoming'])
  )
};

const keyMap = Object.freeze({
  [MY_COMPLETED_CHALLENGES]: 'completed',
  [MY_LIVE_CHALLENGES]: 'live',
  [MY_UPCOMING_CHALLENGES]: 'upcoming'
});

function apiRequestReducer(state, action) {
  const key = keyMap[action.api];

  if (key) {
    return {
      [key]: {
        ...state[key],
        errorMessages: [],
        isFetching: true
      }
    };
  }

  if (action.api === DELETE_CHALLENGES) {
    return {
      isDeletingChallenges: true,
      deletingIds: action.ids,
      successMessage: null
    };
  }

  if (action.api === CHALLENGE_ENTRIES) {
    return {
      entries: {
        ...state.entries,
        [action.id]: {
          isLoading: !action.quiet,
          errorMessages: []
        }
      }
    };
  }

  return undefined;
}

function apiSuccessReducer(state, action) {
  const key = keyMap[action.api];

  if (key) {
    return {
      [key]: {
        ...state[key],
        challenges: keyBy(
          'id',
          map(item => decodeChallenge(item, action.meta), action.data)
        ),
        currentPage: get('pagination.current_page', action.meta),
        errorMessages: [],
        hasNextPage: get('pagination.has_next_page', action.meta),
        isFetching: false,
        lastPage: get('pagination.last_page', action.meta)
      }
    };
  }

  if (action.api === DELETE_CHALLENGES) {
    return {
      isDeletingChallenges: false,
      deletingIds: [],
      successMessage: action.data.message,
      upcoming: {
        ...state.upcoming,
        challenges: omitBy(ch => state.deletingIds.includes(ch.id))(state.upcoming.challenges)
      }
    };
  }

  if (action.api === CHALLENGE_ENTRIES) {
    return {
      entries: {
        ...state.entries,
        [action.id]: {
          isLoading: false,
          items: action.data.map(decodeChallengeEntry)
        }
      }
    };
  }

  return undefined;
}

function apiFailureReducer(state, action) {
  const key = keyMap[action.api];

  if (key) {
    return {
      [key]: {
        ...state[key],
        errorMessages: mapErrorMessages(action.errors),
        isFetching: false
      }
    };
  }

  if (action.api === DELETE_CHALLENGES) {
    return {
      isDeletingChallenges: false,
      upcoming: {
        ...state.upcoming,
        errorMessages: mapErrorMessages(action.errors)
      }
    };
  }

  if (action.api === CHALLENGE_ENTRIES) {
    return {
      entries: {
        ...state.entries,
        [action.id]: {
          isLoading: false,
          errorMessages: mapErrorMessages(action.errors)
        }
      }
    };
  }

  return undefined;
}

function challengeReducer(state, action) {
  // we show live challenges in the right sidebar and we want to keep them in sync
  // so if we load a challenge independant of the poll that is in the sidebar
  // make sure it stays in sync
  const challenge = decodeChallenge(action.data, action.meta);

  if (state.live.challenges[challenge.id]) {
    return {
      live: {
        challenges: set(challenge.id, challenge, state.live.challenges)
      }
    };
  }

  return undefined;
}

const myChallengesReducer = createReducer(initialState, {
  [API_CALL_REQUEST]: apiRequestReducer,
  [API_CALL_SUCCESS]: apiSuccessReducer,
  [API_CALL_FAILURE]: apiFailureReducer,
  [CHALLENGE]: challengeReducer
});

export default myChallengesReducer;
